import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { organizationService } from '@/services/organization.service'
import type { CreateOrganizationInput } from '@/types'

export const ORGANIZATION_QUERY_KEY = ['organization']

export function useOrganization() {
  return useQuery({
    queryKey: [...ORGANIZATION_QUERY_KEY, 'current'],
    queryFn: () => organizationService.getCurrentOrganization()
  })
}

export function useEnsureUserSetup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => organizationService.ensureUserSetup(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
    }
  })
}

export function useOrganizationMembers(organizationId: string | undefined) {
  return useQuery({
    queryKey: [...ORGANIZATION_QUERY_KEY, 'members', organizationId],
    queryFn: () => organizationService.getOrganizationMembers(organizationId!),
    enabled: !!organizationId
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateOrganizationInput> }) =>
      organizationService.updateOrganization(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
    }
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'owner' | 'admin' | 'member' }) =>
      organizationService.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ORGANIZATION_QUERY_KEY, 'members'] })
    }
  })
}

export function useRemoveUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) =>
      organizationService.removeUserFromOrganization(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ORGANIZATION_QUERY_KEY, 'members'] })
    }
  })
}
